import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import io.github.ele4326.emilyeby.components.layouts.MarkdownLayout as _io_github_ele4326_emilyeby_components_layouts_MarkdownLayout
import io.github.ele4326.emilyeby.components.layouts.PageLayout as _io_github_ele4326_emilyeby_components_layouts_PageLayout
import io.github.ele4326.emilyeby.pages.AboutMePage as _io_github_ele4326_emilyeby_pages_AboutMePage
import io.github.ele4326.emilyeby.pages.AboutPage as _io_github_ele4326_emilyeby_pages_AboutPage
import io.github.ele4326.emilyeby.pages.ClimateInMotionPage as _io_github_ele4326_emilyeby_pages_ClimateInMotionPage
import io.github.ele4326.emilyeby.pages.DuolingoPage as _io_github_ele4326_emilyeby_pages_DuolingoPage
import io.github.ele4326.emilyeby.pages.HomePage as _io_github_ele4326_emilyeby_pages_HomePage
import io.github.ele4326.emilyeby.pages.InTheBuzzPage as _io_github_ele4326_emilyeby_pages_InTheBuzzPage
import io.github.ele4326.emilyeby.pages.ProjectsPage as _io_github_ele4326_emilyeby_pages_ProjectsPage
import io.github.ele4326.emilyeby.pages.TrustResearchPage as _io_github_ele4326_emilyeby_pages_TrustResearchPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "EmilyEby"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "io.github.ele4326.emilyeby.components.layouts.MarkdownLayout",
            parentLayoutId = "io.github.ele4326.emilyeby.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                io.github.ele4326.emilyeby.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _io_github_ele4326_emilyeby_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "io.github.ele4326.emilyeby.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _io_github_ele4326_emilyeby_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "io.github.ele4326.emilyeby.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                io.github.ele4326.emilyeby.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "io.github.ele4326.emilyeby.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                io.github.ele4326.emilyeby.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_AboutPage()
        }
        ctx.router.register(
            "/aboutMe",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_AboutMePage()
        }
        ctx.router.register(
            "/climateInMotion",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_ClimateInMotionPage()
        }
        ctx.router.register(
            "/duolingo",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_DuolingoPage()
        }
        ctx.router.register(
            "/inTheBuzz",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_InTheBuzzPage()
        }
        ctx.router.register(
            "/projects",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_ProjectsPage()
        }
        ctx.router.register(
            "/trustResearch",
        ) { pageCtx -> 
            _io_github_ele4326_emilyeby_pages_TrustResearchPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", io.github.ele4326.emilyeby.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", io.github.ele4326.emilyeby.SubheadlineTextStyle)
        ctx.theme.registerStyle("header3-sans-serif-text", io.github.ele4326.emilyeby.Header3SansSerifTextStyle)
        ctx.theme.registerStyle("header4-sans-serif-text", io.github.ele4326.emilyeby.Header4SansSerifTextStyle)
        ctx.theme.registerStyle("body2-sans-serif-text", io.github.ele4326.emilyeby.Body2SansSerifTextStyle)
        ctx.theme.registerStyle("body3-sans-serif-text", io.github.ele4326.emilyeby.Body3SansSerifTextStyle)
        ctx.theme.registerStyle("footer", io.github.ele4326.emilyeby.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", io.github.ele4326.emilyeby.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("markdown", io.github.ele4326.emilyeby.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("page-content", io.github.ele4326.emilyeby.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("hero-container", io.github.ele4326.emilyeby.pages.HeroContainerStyle)
        ctx.theme.registerStyle("home-grid", io.github.ele4326.emilyeby.pages.HomeGridStyle)
        ctx.theme.registerStyle("home-grid-cell", io.github.ele4326.emilyeby.pages.HomeGridCellStyle)
        ctx.theme.registerVariant("-circle", io.github.ele4326.emilyeby.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", io.github.ele4326.emilyeby.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", io.github.ele4326.emilyeby.components.sections.SideMenuSlideInAnim)
        io.github.ele4326.emilyeby.initSiteStyles(ctx)
        io.github.ele4326.emilyeby.initColorMode(ctx)
        io.github.ele4326.emilyeby.initStyles(ctx)
        io.github.ele4326.emilyeby.initTheme(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        io.github.ele4326.emilyeby.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
